import Vue from "vue";

import { machinesApi } from "@/api/machinesApi";
import {
  isCmsMachine,
  isCmsVariant,
  cmsVariant
} from "@/stores/modules/machineComponents/machineComponentsHelper";
import { streamApiCore } from "@/plugins/streamCore";
import { selectorToDate } from "@/stores/Utils";
import { parseXMLStringToComponents } from "@/stores/modules/machineComponents/machineDataModelParser";
import { extendComponentsWithAdditionalInfo } from "./machineDataModelExtra";

const topicDetailFromNode = node => {
  let result = undefined;

  if (node) {
    const { topic, topicLevel, variant, description } = node;
    const count = (node && node.count) || 0;
    const period = (node && node.period) || null;

    result = (topic || []).map(el => {
      const cmsVariantCheck = isCmsVariant(variant);
      const minutes = null;
      let hours = null;
      let title = "";

      if (cmsVariantCheck) {
        hours = 8;
      } else if (el.includes("Machine/Spindle/Realtime/Status")) {
        title = "Status";
      } else if (
        el.includes("Machine/Spindle/Realtime/TemperatureStatus") ||
        el.includes("Machine/Spindle/Realtime/Temperature")
      ) {
        title = "Temperature";
      } else if (
        el.includes("Machine/Spindle/Realtime/VibrationStatus") ||
        el.includes("Machine/Spindle/Realtime/VibrationLevel")
      ) {
        title = "Vibration";
      } else if (el.includes("Machine/Spindle/Realtime/Speed")) {
        title = "Speed";
        hours = 8;
      } else if (el.includes("Machine/OperatingGroups/01/Load")) {
        title = "Load";
      } else if (el.includes("Machine/OperatingGroups/01/ActiveTime")) {
        title = "ActiveTime";
      }

      let type = undefined;
      let filter = undefined;

      if (count > 0) {
        type = "count";
        filter = count;
      } else if (period) {
        type = "period";
        filter = selectorToDate(period);
      } else if (hours || minutes) {
        type = "range";
        const now = new Date();
        if (hours) {
          now.setHours(now.getHours() - hours);
        }
        if (minutes) {
          now.setMinutes(now.getMinutes() - minutes);
        }
        filter = { after: now };
      }

      return {
        title: title || topicLevel,
        description,
        topicLevel,
        data: [],
        channel: el,
        variant,
        type,
        filter
      };
    });
  }

  return result;
};

const spindleSensorSeriesFromComponents = components => {
  const children = components && components.children;
  const result = (children || [])
    .filter(el => {
      return el.topicLevel === "Spindle_Sensor";
    })
    .reduce((acc, el) => {
      return [...acc, ...el.children];
    }, [])
    .reduce((acc, el) => {
      return [...acc, ...topicDetailFromNode(el)];
    }, []);

  return result;
};

const deepNodeSearchWithVariant = (node, variant = []) => {
  let result = undefined;
  const children = (node && node.children) || [];

  if (node) {
    if (variant.includes(node.variant)) {
      result = node;
    } else {
      result = children
        .map(el => deepNodeSearchWithVariant(el, variant))
        .reduce((acc, el) => {
          if (el) {
            if (Array.isArray(el)) {
              acc = [...acc, ...el];
            } else {
              acc = [...acc, el];
            }
          }
          return acc;
        }, []);
    }
  }

  return result;
};

const cmsSeriesFromComponents = components => {
  const cmsNodes = deepNodeSearchWithVariant(components, cmsVariant);
  const result = cmsNodes || [];
  return result
    .reduce((acc, el) => {
      return [...acc, ...el.children];
    }, [])
    .reduce((acc, el) => {
      return [...acc, ...el.children];
    }, [])
    .filter(el => ["SpeedRate", "Load", "SpeedOverride", "Status", "Mode"].includes(el.topicLevel))
    .reduce((acc, el) => {
      return [...acc, ...topicDetailFromNode(el)];
    }, []);
};

const parsePayload = payload => {
  const channel = (payload && payload.channel) || "";
  const data = (payload && payload.data) || payload || {};

  const ts = new Date(data.ts).getTime();
  let result = undefined;

  if (typeof data.v === "object" && data.v) {
    result = [ts, data.v.value, data.v.status];
  } else {
    let v = String(data.v);
    if (!isNaN(v.replace(",", ".").replace(" ", ""))) {
      // Is number
      v = Number(v.replace(",", ".").replace(" ", ""));
    }
    // SpindleActiveTime require values in "h" but recives "s"
    if (channel.includes("/ActiveTime")) {
      v = v / 3600;
    }
    result = [ts, v];
  }

  return result;
};

export default {
  state: {
    loading: false,
    components: {},
    tableData: {},
    selectedNode: undefined,
    activeNodeId: undefined,
    series: {},
    subscriptions: []
  },
  getters: {
    isLoading: state => {
      return state.loading;
    },
    getSeriesByMachineId: state => machineId => {
      return state.series[machineId];
    },
    getComponentsByMachineId: state => machineId => {
      return state.components[machineId];
    },
    getTableDataByMachineId: state => machineId => {
      return state.tableData[machineId];
    },
    selectedNode(state) {
      return state.selectedNode;
    },
    activeNodeId(state) {
      return state.activeNodeId;
    }
  },
  actions: {
    async fetchMachineComponents({ commit, dispatch }, { machine, enableSensorsSubscription }) {
      await dispatch("clearSelectedNode");
      if (machine) {
        commit("SET_LOADING", true);

        try {
          const machineId = machine.Id;
          const data = await machinesApi.getMachineDataModel(machineId);
          const xml = data && data.value;

          const components = await parseXMLStringToComponents(xml);
          const result = await extendComponentsWithAdditionalInfo(
            components,
            machine,
            enableSensorsSubscription
          );

          if (enableSensorsSubscription) {
            dispatch("subscribeSensors", {
              machine,
              components
            });
          }
          commit("UPDATE_COMPONENTS", {
            machineId,
            payload: result
          });
          commit("UPDATE_TABLE_DATA", {
            machineId,
            payload: result
          });
        } catch (e) {
          console.error("[Error] Fetch machine components by machine failed.", e);
        }

        commit("SET_LOADING", false);
      }
    },
    selectNode({ commit }, node) {
      commit("SELECT_NODE", {
        node
      });
    },
    activeNodeId({ commit }, nodeId) {
      commit("ACTIVE_NODE_ID", {
        nodeId
      });
    },
    clearSelectedNode({ commit }) {
      commit("SELECT_NODE", { node: undefined });
    },
    subscribeSensors({ commit }, { machine, components }) {
      const machineId = machine && machine.Id;
      const cmsSeries = isCmsMachine(machine) ? cmsSeriesFromComponents(components) : [];
      const spindleSensorSeries = spindleSensorSeriesFromComponents(components);
      const series = [...spindleSensorSeries, ...cmsSeries];

      commit("INIT_SERIES", { machineId, series });

      const subscriptions = series.map(el => {
        const { type, channel, filter } = el;
        let streamId = undefined;

        if (type === "count") {
          streamId = streamApiCore.subLastCount(machine, channel, filter, payload => {
            commit("UPDATE_SERIES", { machineId, payload });
          });
        } else if (type === "period") {
          streamId = streamApiCore.subFrom(machine, channel, filter, payload => {
            commit("UPDATE_SERIES", { machineId, payload });
          });
        } else if (type === "range") {
          streamId = streamApiCore.retrieveHistorical(machine, channel, filter, arr => {
            commit("UPDATE_SERIES", {
              machineId,
              payload: {
                channel,
                data: arr
              }
            });

            // Last
            let isFirst = true;
            streamApiCore.subLatest(machine, channel, payload => {
              if (isFirst) {
                isFirst = false;
              } else {
                commit("UPDATE_SERIES", { machineId, payload });
              }
            });
          });
        } else {
          streamId = streamApiCore.subLatest(machine, channel, payload => {
            commit("UPDATE_SERIES", { machineId, payload });
          });
        }

        return streamId;
      });

      commit("UNREGISTER_SUBSCRIPTIONS");
      commit("REGISTER_SUBSCRIPTIONS", subscriptions);
    },
    reset({ commit }) {
      commit("UNREGISTER_SUBSCRIPTIONS");
      commit("RESET");
    }
  },
  mutations: {
    SET_LOADING: (state, loading) => {
      Vue.set(state, "loading", loading);
    },
    UNREGISTER_SUBSCRIPTIONS: state => {
      if (state.subscriptions.length > 0) {
        state.subscriptions.forEach(el => {
          streamApiCore.unsub(el);
        });
      }

      Vue.set(state, "subscriptions", []);
    },
    REGISTER_SUBSCRIPTIONS: (state, subscriptions) => {
      Vue.set(state, "subscriptions", subscriptions || []);
    },
    INIT_SERIES: (state, { machineId, series }) => {
      Vue.set(state.series, machineId, series || []);
    },
    UPDATE_SERIES: (state, { machineId, payload }) => {
      if (payload) {
        const index = state.series[machineId].findIndex(series =>
          `${payload.channel}`.includes(series.channel)
        );

        if (index >= 0) {
          const updateSeries = { ...state.series[machineId][index] };
          let data = undefined;

          if (
            updateSeries.data &&
            Array.isArray(updateSeries.data) &&
            updateSeries.data.length > 0
          ) {
            updateSeries.data.pop();
          }

          if (Array.isArray(payload.data)) {
            data = payload.data.map(el => parsePayload(el));
          } else {
            // Add new value to serie
            data = [...updateSeries.data, parsePayload(payload)];
          }

          const newData = data
            .sort((a, b) => (a[0] > b[0] ? 1 : -1))
            .filter((e, i, a) => {
              const valid = i > 0 ? e[0] !== a[i - 1][0] : true;
              return valid;
            });

          if (newData.length > 0) {
            const lastValue = [...newData[newData.length - 1]];
            const now = Date.now();
            if (now > lastValue[0]) {
              lastValue[0] = Date.now();
              lastValue[1] = isNaN(lastValue[1]) ? lastValue[1] : 0;
            }

            updateSeries.data = [...newData, lastValue];
          } else {
            updateSeries.data = [...newData];
          }

          Vue.set(state.series[machineId], index, updateSeries);
        }
      }
    },
    UPDATE_COMPONENTS: (state, { machineId, payload }) => {
      Vue.set(state.components, machineId, payload.components);
    },
    UPDATE_TABLE_DATA: (state, { machineId, payload }) => {
      Vue.set(state.tableData, machineId, payload.tableData);
    },
    SELECT_NODE: (state, { node }) => {
      Vue.set(state, "selectedNode", node);
    },
    ACTIVE_NODE_ID: (state, { nodeId }) => {
      Vue.set(state, "activeNodeId", nodeId);
    },
    RESET: state => {
      state.loading = false;
      state.components = {};
      state.tableData = {};
      state.selectedNode = undefined;
      state.activeNodeId = undefined;
      state.series = {};
      state.subscriptions = [];
    }
  },
  namespaced: true,
  modules: {}
};
